import { reactive, watchEffect } from 'vue';
import _ from 'lodash';
import ESARSite, { fileClassifications } from '@/models/ESARSite';
import Api from '@/api';

export type SearchType = 'ATS' | 'PBL' | 'Operation Name' |'ESRD File Number' | 'Municipality' | 'City' | 'Neighborhood' | '';
export type SuggestionResult = {
    title: string;
    type: SearchType;
    item: ESARSite;
};

const appState: {
    openSite: ESARSite | null, // id of site currently opened in list
    hoveredSite: ESARSite | null, // site user is hovering over on the list
    filteredStatusCode: string, // selected classification for filtering entries (CSU, REC, PST or SCD)
    filteredStatusDescription: string,
    isInit: boolean,
    isSearching: boolean,
    currentSearchQuery: string
    currentSearchType: SearchType,
    allSitesList: Array<ESARSite>, // list of all sites
    listViewSites: Array<ESARSite>, // list of all sites displayed in list view
    mapViewSites: Array<ESARSite> // list of all sites for map view
} = reactive({
    openSite: null,
    hoveredSite: null,
    filteredStatusCode: '',
    filteredStatusDescription: '',
    isInit: false,
    isSearching: false,
    currentSearchQuery: '',
    currentSearchType: '',
    allSitesList: [],
    listViewSites: [],
    mapViewSites: []
});

export type AppState = typeof appState;

export function useAppState(): AppState {
    return appState;
}
function app() {
    const state = useAppState();

    // const model = reactive<ESARSiteManagerModel>(new ESARSiteManagerModel());

    const { fetchSites } = Api();
    fetchSites().then((sitesList) => {
        state.allSitesList = sitesList
            .map((s) => new ESARSite(s))
            .filter(site => fileClassifications[site.fileClassification]);
        state.listViewSites = [...state.allSitesList];
        state.mapViewSites = [...state.allSitesList];
        state.isInit = true;
    });
    watchEffect(() => fetchSites());

    // state mutator methods
    const setOpenSite = function (site: ESARSite | null = null): void {
        if (site && (state.openSite === null || site.id !== state.openSite.id)) {
            state.openSite = site;
        } else {
            state.openSite = null;
        }
    };

    const clearOpenSite = function (): void {
        state.openSite = null;
    };

    const getSearchSuggestions = function (_query: string, searchType: SearchType, maxResults = -1): SuggestionResult[] {
        const query = _query.toLowerCase();
        const suggestions = _.chain(state.allSitesList)
            .filter((i) => {
                if (state.filteredStatusCode === '') {
                    return true;
                }
                return i.fileClassification === state.filteredStatusCode;
            })
            .map((i) => {
                let type: SearchType = '';
                let title = '';

                if (query === '') {
                    type = 'ESRD File Number';
                    return {
                        title,
                        type,
                        item: i,
                    };
                }

                switch (searchType) {
                case 'ATS': {
                    const foundATS = i.LLD.find((j) => j.ATS?.toLowerCase().includes(query));
                    if (foundATS) {
                        type = 'ATS';
                        title = foundATS?.ATS || '';
                    }
                    break;
                }
                case 'PBL': {
                    const foundPBL = i.LLD.find((j) => j.PBL?.toLowerCase().includes(query));
                    if (foundPBL) {
                        type = 'PBL';
                        title = foundPBL?.PBL || '';
                    }
                    break;
                }
                default:
                    if (i.id.toLowerCase() === query) {
                        type = 'ESRD File Number';
                        title = i.id;
                    } else if (i.operationName.toLowerCase().includes(query)) {
                        type = 'Operation Name';
                        title = i.operationName;
                    } else if (i.municipality.toLowerCase().includes(query)) {
                        type = 'Municipality';
                        title = `${i.municipality}`;
                    } else if (i.city.toLowerCase().includes(query)) {
                        type = 'Municipality';
                        title = `${i.city}`;
                    } else if (i.neighborhood.toLowerCase().includes(query)) {
                        type = 'Neighborhood';
                        title = `${i.neighborhood}`;
                    } else {
                        const foundATS = i.LLD.find((j) => j.ATS?.toLowerCase().includes(query));
                        const foundPBL = i.LLD.find((j) => j.PBL?.toLowerCase().includes(query));
                        if (foundATS) {
                            type = 'ATS';
                            title = foundATS?.ATS || '';
                        } else if (foundPBL) {
                            type = 'PBL';
                            title = foundPBL?.PBL || '';
                        }
                    }
                    break;
                }
                return {
                    title,
                    type,
                    item: i,
                };
            }).filter((i) => i.type !== '')
            .uniqWith((arrVal, othVal) => arrVal.title === othVal.title && arrVal.type === othVal.type)
            .value();
        if (maxResults !== -1) {
            return suggestions.slice(0, maxResults);
        }
        return suggestions;
    };

    const doSearch = function (_query: string, searchType: SearchType): void {
        state.isSearching = true;
        const query = _query.toLowerCase();
        state.currentSearchQuery = query;
        state.listViewSites = _.chain(state.allSitesList)
            .filter((i) => {
                if (state.filteredStatusCode === '') {
                    return true;
                }
                return i.fileClassification === state.filteredStatusCode;
            })
            .filter((i) => {
                if (query === '') {
                    return true;
                }
                switch (searchType) {
                case 'ATS': {
                    const foundATS = i.LLD.find((j) => j.ATS?.toLowerCase().includes(query));
                    return foundATS !== undefined;
                }
                case 'PBL': {
                    const foundPBL = i.LLD.find((j) => j.PBL?.toLowerCase().includes(query));
                    return foundPBL !== undefined;
                }
                case 'Municipality': {
                    return i.municipality.toLowerCase() === query
                            || i.city.toLowerCase() === query;
                }
                case 'Neighborhood':
                    return i.neighborhood.toLowerCase() === query;
                default: {
                    if (i.id.toLowerCase() === query
                    || i.operationName.toLowerCase().includes(query)
                    || i.municipality.toLowerCase().includes(query)
                    || i.city.toLowerCase().includes(query)
                    || i.neighborhood.toLowerCase().includes(query)
                    || i.LLD.find((j) => j.ATS?.toLowerCase().includes(query))
                    || i.LLD.find((j) => j.PBL?.toLowerCase().includes(query))) {
                        return true;
                    }
                    return false;
                }
                }
            }).value();
        state.mapViewSites = [...state.listViewSites];
        setTimeout(() => {
            state.isSearching = false;
        }, 500);
    };

    const clearSearch = function (): void {
        state.openSite = null;
        doSearch('', '');
    };

    const loadSuggestionResult = function (suggestion: SuggestionResult): void {
        state.listViewSites = [suggestion.item];
        state.mapViewSites = [suggestion.item];
        state.currentSearchQuery = suggestion.title;
        setOpenSite(suggestion.item);
    };

    const toggleFilterStatus = function (classificationCode = '', classificationDesc = '') {
        state.openSite = null;
        if (classificationCode === state.filteredStatusCode) {
            state.filteredStatusCode = '';
            state.filteredStatusDescription = '';
        } else {
            state.filteredStatusCode = classificationCode;
            state.filteredStatusDescription = classificationDesc;
        }
        doSearch(state.currentSearchQuery, state.currentSearchType);
    };

    const setHoveredSite = function (site: ESARSite) {
        state.hoveredSite = site;
    };

    const removeHoveredSite = function () {
        state.hoveredSite = null;
    };

    return {
        state,
        setOpenSite,
        toggleFilterStatus,
        clearOpenSite,
        getSearchSuggestions,
        doSearch,
        clearSearch,
        loadSuggestionResult,
        setHoveredSite,
        removeHoveredSite
    };
}

const appSingleton = app();
export default function useApp(): typeof appSingleton {
    // NOTE: Any setup hooks must be called here.
    // This gets called from every setup() that uses the app.
    return appSingleton;
}
